import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { FONT_PAIRINGS, type ThemeFontPairing } from '@/lib/landing-theme-constants';

interface FontPairingPickerProps {
  value: string;
  onChange: (fontPairingId: string) => void;
}

/**
 * Preloads Google Fonts for all pairings so previews render immediately.
 */
function usePreloadFonts() {
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    FONT_PAIRINGS.forEach((fp) => {
      const id = `font-preview-${fp.id}`;
      if (document.getElementById(id)) return;
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = fp.googleFontsUrl;
      document.head.appendChild(link);
    });
  }, []);
}

const FontCard: React.FC<{ fp: ThemeFontPairing; isSelected: boolean; onSelect: () => void }> = ({
  fp,
  isSelected,
  onSelect,
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`relative rounded-lg border-2 p-3 text-left transition-all hover:shadow-md ${
      isSelected
        ? 'border-primary ring-2 ring-primary/20 shadow-sm'
        : 'border-border hover:border-primary/40'
    }`}
  >
    {isSelected && (
      <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
        <Check className="h-3 w-3" />
      </div>
    )}
    {/* Font preview */}
    <div className="mb-2 space-y-0.5">
      <div
        className="text-lg font-bold leading-tight"
        style={{ fontFamily: `'${fp.headingFont}', serif` }}
      >
        {fp.headingFont}
      </div>
      <div
        className="text-sm text-muted-foreground leading-snug"
        style={{ fontFamily: `'${fp.bodyFont}', sans-serif` }}
      >
        The quick brown fox jumps over the lazy dog.
      </div>
    </div>
    <div className="text-xs font-semibold text-foreground">{fp.name}</div>
    <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
      {fp.headingFont} + {fp.bodyFont}
    </div>
  </button>
);

export const FontPairingPicker: React.FC<FontPairingPickerProps> = ({ value, onChange }) => {
  usePreloadFonts();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {FONT_PAIRINGS.map((fp) => (
        <FontCard
          key={fp.id}
          fp={fp}
          isSelected={value === fp.id}
          onSelect={() => onChange(fp.id)}
        />
      ))}
    </div>
  );
};
